'use client';
import { useEffect } from 'react';
import delay from 'lodash-es/delay';

import { IRepository } from '@/types';
import { IDataFrameResponse } from '@/lib/dataFrameTypes';
import { IEvaluation } from '@/lib/evaluationsApi';

import { DataframeQueryContextProvider } from '@/components/DataFrame/DataframeQueryContext';
import ClientWrapper from '@/components/DataFrame/DataframeLoader/ClientWrapper';
import useSampleDataframe from '@/hooks/useSampleDataframe';
import useRmodDataframe from '@/hooks/useRmodDataframe';
import useEvaluationDataframe from '@/hooks/useEvaluationDataframe';

const SHORT_POLLING_INTERVAL = 1 * 1000; // 1 second
const LONG_POLLING_INTERVAL = 10 * 1000; // 10 seconds

const useRevalidateAfterCompletion = (
  status: string,
  revalidate: () => void,
  interval: number = SHORT_POLLING_INTERVAL,
) => {
  useEffect(() => {
    // revalidate once when the evaluation is completed needed to ensure that the last batch of data is retrieved
    if (status === 'completed') {
      delay(() => {
        revalidate();
      }, interval);
    }
  }, [status]);
};

const LoaderDataFrameView: React.FC<{
  repository: IRepository;
  evaluation: IEvaluation;
  dataFrameResponse?: IDataFrameResponse;
}> = ({ repository, evaluation, dataFrameResponse }) => {
  const fileRootPath = `${repository.namespace}/${repository.name}/file/${evaluation.resource.version}`;
  const { data, error, revalidate } = useEvaluationDataframe({
    repository,
    evaluation,
    initialData: dataFrameResponse,
  });
  return data ? (
    <DataframeQueryContextProvider searchParams={new URLSearchParams()} sortable={false}>
      <ClientWrapper
        dataFrameResponse={data as unknown as IDataFrameResponse}
        fileRootPath={fileRootPath}
        mode="sample"
        repository={repository}
        layoutSettings={{ detailsPaneVisible: false, schemaPaneVisible: false, openDetailsOnCellFocus: true }}
      />
    </DataframeQueryContextProvider>
  ) : (
    <div>Loading...</div>
  );
};

const WorkspaceDataFrameView: React.FC<{
  repository: IRepository;
  evaluation: IEvaluation;
}> = ({ repository, evaluation }) => {
  const fileRootPath = `${repository.namespace}/${repository.name}/file/${evaluation.resource.version}`;
  const { data, error, revalidate, isLoading } = useRmodDataframe({
    repository,
    evaluation,
  });
  useRevalidateAfterCompletion(evaluation?.status, revalidate, LONG_POLLING_INTERVAL);
  console.log('WorkspaceDataFrameView > data', data);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (data && !data?.data_frame) {
    // TODO: add something to track this - so we can see how often this happens
    return <div>The requested dataframe was not returned from the server. </div>;
  }

  return (
    <DataframeQueryContextProvider searchParams={new URLSearchParams()} sortable={false}>
      <ClientWrapper
        dataFrameResponse={data as unknown as IDataFrameResponse}
        fileRootPath={fileRootPath}
        mode="sample"
        repository={repository}
        layoutSettings={{ detailsPaneVisible: false, schemaPaneVisible: false, openDetailsOnCellFocus: true }}
        evaluationTargetField={evaluation?.target_column}
      />
    </DataframeQueryContextProvider>
  );
};

const SampleDataFrameView: React.FC<{
  repository: IRepository;
  evaluation: IEvaluation;
  dataFrameResponse?: IDataFrameResponse;
}> = ({ repository, evaluation, dataFrameResponse }) => {
  const fileRootPath = `${repository.namespace}/${repository.name}/file/${evaluation.resource.version}`;
  const { data, error, revalidate } = useSampleDataframe({
    repository,
    evaluationId: evaluation?.id as string,
    initialData: dataFrameResponse,
    status: evaluation?.status,
  });

  useRevalidateAfterCompletion(evaluation?.status, revalidate);

  return (
    data && (
      <DataframeQueryContextProvider searchParams={new URLSearchParams()} sortable={false}>
        <ClientWrapper
          fileRootPath={fileRootPath}
          dataFrameResponse={data as unknown as IDataFrameResponse}
          mode="sample"
          repository={repository}
          layoutSettings={{ detailsPaneVisible: false, schemaPaneVisible: false, openDetailsOnCellFocus: true }}
          evaluationTargetField={evaluation?.target_column}
        />
      </DataframeQueryContextProvider>
    )
  );
};

export default {
  Sample: SampleDataFrameView,
  Workspace: WorkspaceDataFrameView,
  Loader: LoaderDataFrameView,
};
